import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import AxiosInstance from "../Home/AxiosInstance";
import { clearAuthSession } from "../Home/auth";
import Profile from "../Home/Profile.png";
import "./EmployerHeader.css";
import logo from "../Home/logo.png";

export default function AdminHeader() {
  const [userId, setUserId] = useState(null);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [popupVisible, setPopupVisible] = useState(false);
  const [editingMode, setEditingMode] = useState(false);
  const [editedDetails, setEditedDetails] = useState(null);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const profileImageSrc = userDetails?.image_path;

  useEffect(() => {
    const storedUserId = localStorage.getItem("userId");
    if (storedUserId) {
      setUserId(storedUserId);
      fetchUserDetails(storedUserId);
    }
  }, []);

  const fetchUserDetails = async (userId) => {
    try {
      const response = await AxiosInstance.get(
        `companies/user/details/${userId}/`
      );
      setUserDetails(response.data);
      setEditedDetails(response.data);
    } catch (error) {
      console.error("Error fetching user details", error);
    }
  };

  const toggleDropdown = () => {
    setDropdownVisible(!dropdownVisible);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownVisible(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleProfile = () => {
    setPopupVisible(true);
    setDropdownVisible(false);
  };

  const handleEdit = () => {
    setEditingMode(true);
  };

  const handleEditChange = (e) => {
    setEditedDetails({ ...editedDetails, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    const currentTime = new Date().toISOString();

    const formData = new FormData();
    formData.append("first_name", editedDetails.first_name);
    formData.append("last_name", editedDetails.last_name);
    formData.append("email", editedDetails.email);
    formData.append("mobile_no", editedDetails.mobile_no);
    formData.append("dob", editedDetails.dob);
    formData.append("gender", editedDetails.gender);
    formData.append("updated_at", currentTime);
    formData.append("updated_by_id", userId);

    if (editedDetails.image_path instanceof File) {
      formData.append("image_path", editedDetails.image_path);
    }

    try {
      const response = await AxiosInstance.put(
        `companies/user/update/${userId}/`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      setUserDetails(response.data);
      setEditingMode(false);
    } catch (error) {
      console.error("Error updating user details", error);
    }
  };

  const handleChangePassword = () => {
    navigate("/change-password", {
      state: {
        userType: "user",
        userId: userId,
      },
    });
    setDropdownVisible(false);
  };

  const handleSignOut = () => {
    clearAuthSession();

    const isInstitutionPage = location.pathname.includes("institution");

    if (isInstitutionPage) {
      navigate("/institutions");
    } else {
      navigate("/employers");
    }

    setDropdownVisible(false);
  };

  const isProfilePage = location.pathname === "/profile";
  const isInstitutionPage = location.pathname.includes("institution");
  const fullName = userDetails
    ? `${userDetails.first_name || ""} ${userDetails.last_name || ""}`.trim()
    : "";
  const avatarSrc = profileImageSrc
    ? `${AxiosInstance.defaults.baseURL}/media/${profileImageSrc}`
    : Profile;

  return (
    <header className="employer-header">
      <div className="employer-header-inner">
        <div className="employer-brand">
          <img src={logo} alt="Open Learnings" className="employer-brand-logo" />
          <span className="employer-brand-text">
            <strong>Open Learnings</strong>
            {isInstitutionPage && <small>Institute admin</small>}
          </span>
        </div>

        {!isProfilePage && userDetails && (
          <div className="employer-header-user" ref={dropdownRef}>
            <button
              type="button"
              className="employer-user-btn"
              onClick={toggleDropdown}
              aria-expanded={dropdownVisible}
            >
              <span className="employer-user-meta">
                <strong>{fullName || "Admin"}</strong>
                <small>Admin</small>
              </span>
              <img
                src={avatarSrc}
                alt=""
                className="employer-profile-image"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = Profile;
                }}
              />
            </button>

            {dropdownVisible && (
              <div className="employer-dropdown">
                <button type="button" onClick={handleProfile}>
                  My Details
                </button>
                <button type="button" onClick={handleChangePassword}>
                  Change Password
                </button>
                <button
                  type="button"
                  className="employer-signout"
                  onClick={handleSignOut}
                >
                  Sign Out
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {popupVisible && userDetails && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {userDetails.first_name} {userDetails.last_name}
                </h5>
              </div>
              <div className="modal-body">
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <img
                    src={avatarSrc}
                    alt="Profile"
                    className="img-thumbnail rounded-circle mb-3"
                    style={{ width: "120px", height: "120px" }}
                  />
                </div>
                <div className="text-start">
                  <p>
                    <strong>Email:</strong> {userDetails.email}
                  </p>
                  <p>
                    <strong>Mobile No:</strong> {userDetails.mobile_no}
                  </p>
                  <p>
                    <strong>Date Of Birth:</strong> {userDetails.dob}
                  </p>
                  <p>
                    <strong>Gender:</strong> {userDetails.gender}
                  </p>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-primary" onClick={handleEdit}>
                  Edit
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => setPopupVisible(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {editingMode && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Admin Details</h5>
              </div>
              <div className="modal-body">
                <form onSubmit={(e) => e.preventDefault()}>
                  <div className="row mb-3">
                    <div className="col">
                      <label className="form-label">First Name</label>
                      <input
                        type="text"
                        className="form-control"
                        name="first_name"
                        value={editedDetails.first_name || ""}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="col">
                      <label className="form-label">Last Name</label>
                      <input
                        type="text"
                        className="form-control"
                        name="last_name"
                        value={editedDetails.last_name || ""}
                        onChange={handleEditChange}
                      />
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col">
                      <label className="form-label">Email</label>
                      <input
                        type="email"
                        className="form-control"
                        name="email"
                        value={editedDetails.email || ""}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="col">
                      <label className="form-label">Mobile No</label>
                      <input
                        type="text"
                        className="form-control"
                        name="mobile_no"
                        value={editedDetails.mobile_no || ""}
                        onChange={handleEditChange}
                      />
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col">
                      <label className="form-label">Date of Birth</label>
                      <input
                        type="date"
                        className="form-control"
                        name="dob"
                        value={editedDetails.dob || ""}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="col">
                      <label className="form-label">Gender</label>
                      <select
                        className="form-select"
                        name="gender"
                        value={editedDetails.gender || ""}
                        onChange={handleEditChange}
                      >
                        <option value="">Select</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                      </select>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Profile Image</label>
                    <input
                      type="file"
                      className="form-control"
                      name="image_path"
                      accept="image/*"
                      onChange={(e) =>
                        setEditedDetails({
                          ...editedDetails,
                          image_path: e.target.files[0],
                        })
                      }
                    />
                  </div>

                  <div className="modal-footer">
                    <button className="btn btn-primary" onClick={handleSave}>
                      Save Changes
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={() => setEditingMode(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
